/**
 * Publisher tracking domains — operator page (feature #136).
 *
 * Surface for the /api/v1/publisher-tracking-domains backend. Two regions:
 *
 *   1. Add-domain form — pick affiliate ID, type FQDN, submit. On success
 *      the UI surfaces the exact TXT record the publisher must add at
 *      _trackerity-verify.<domain>. FQDN validation happens client-side
 *      (mirrors server regex) so typos fail before the POST.
 *
 *   2. Verification-status table — one row per registered domain for the
 *      currently scoped affiliate. Each row shows status (pending/verified/
 *      failed), the required TXT record, verified_at, and two actions:
 *      re-run verification, or delete.
 *
 * Brand: emerald → sky gradient headline, Inter, zinc borders, 6-8px rounded,
 * light theme only. No emojis, no competitor names.
 *
 * The file is intentionally monolithic — breaking sub-components into
 * sibling files would widen the owned-file surface. Internal widgets live
 * here as local function components.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	applyVerifyOutcome,
	countByStatus,
	createDomain,
	deleteDomain,
	domainDraftSchema,
	EMPTY_DRAFT,
	formatVerifiedAt,
	listDomains,
	publisherTrackingDomainsQueryKeys,
	sortDomains,
	STATUS_LABELS,
	statusBadgeClass,
	verifyDomain,
	type DomainDraft,
	type PublisherTrackingDomainView,
	type VerifyOutcome,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens (match brand rules)
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Props — deep-link an affiliateId via ?affiliateId= so operators can jump in.
// ---------------------------------------------------------------------------

export interface PublisherTrackingDomainsPageProps {
	initialAffiliateId?: string;
}

export function PublisherTrackingDomainsPage({
	initialAffiliateId,
}: PublisherTrackingDomainsPageProps) {
	const queryClient = useQueryClient();

	const [scope, setScope] = useState<string>(initialAffiliateId ?? "");
	const [draft, setDraft] = useState<DomainDraft>({
		...EMPTY_DRAFT,
		affiliateId: initialAffiliateId ?? "",
	});
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [lastCreated, setLastCreated] =
		useState<PublisherTrackingDomainView | null>(null);
	const [verifyingId, setVerifyingId] = useState<string | null>(null);
	const [lastOutcome, setLastOutcome] = useState<VerifyOutcome | null>(null);

	const trimmedScope = scope.trim();
	const canQuery = trimmedScope.length > 0;

	const listQuery = useQuery({
		queryKey: publisherTrackingDomainsQueryKeys.byAffiliate(trimmedScope),
		queryFn: () => listDomains(trimmedScope),
		enabled: canQuery,
	});

	const createMutation = useMutation({
		mutationFn: createDomain,
		onSuccess: (row) => {
			toast.success(`Domain "${row.domain}" registered.`);
			setLastCreated(row);
			setDraft((prev) => ({ ...prev, domain: "" }));
			setDraftErrors({});
			queryClient.invalidateQueries({
				queryKey: publisherTrackingDomainsQueryKeys.byAffiliate(row.affiliate_id),
			});
			if (row.affiliate_id !== trimmedScope) {
				setScope(row.affiliate_id);
			}
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register domain.");
		},
	});

	const verifyMutation = useMutation({
		mutationFn: (id: string) => verifyDomain(id),
		onMutate: (id: string) => {
			setVerifyingId(id);
		},
		onSettled: () => {
			setVerifyingId(null);
		},
		onSuccess: (outcome) => {
			setLastOutcome(outcome);
			switch (outcome.kind) {
				case "verified":
					toast.success(`Verified ${outcome.domain.domain}.`);
					break;
				case "mismatch":
					toast.error(
						`TXT record for ${outcome.domain.domain} does not match. Expected ${outcome.expected}.`,
					);
					break;
				case "lookup_error":
					toast.error(
						`DNS lookup failed${outcome.domain ? ` for ${outcome.domain.domain}` : ""}: ${outcome.detail}`,
					);
					break;
			}
			queryClient.setQueryData<PublisherTrackingDomainView[]>(
				publisherTrackingDomainsQueryKeys.byAffiliate(trimmedScope),
				(prev) => (prev ? applyVerifyOutcome(prev, outcome) : prev),
			);
			queryClient.invalidateQueries({
				queryKey: publisherTrackingDomainsQueryKeys.byAffiliate(trimmedScope),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Verification failed.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteDomain(id),
		onSuccess: (_void, id) => {
			toast.success("Domain removed.");
			queryClient.setQueryData<PublisherTrackingDomainView[]>(
				publisherTrackingDomainsQueryKeys.byAffiliate(trimmedScope),
				(prev) => (prev ? prev.filter((d) => d.id !== id) : prev),
			);
			queryClient.invalidateQueries({
				queryKey: publisherTrackingDomainsQueryKeys.byAffiliate(trimmedScope),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove domain.");
		},
	});

	const sortedDomains = useMemo(
		() => (listQuery.data ? sortDomains(listQuery.data) : []),
		[listQuery.data],
	);

	const totals = useMemo(() => countByStatus(sortedDomains), [sortedDomains]);

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = domainDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data);
	}

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 pb-16 pt-6">
			<header className="flex flex-col gap-2">
				<h1 className="font-inter bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
					Publisher tracking domains
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Route a partner's clicks through their own hostname. Register the
					vanity domain, show the required TXT record, then verify once the
					publisher has added it to their DNS zone.
				</p>
			</header>

			<section aria-labelledby="add-domain-heading" className={`${CARD} p-6`}>
				<h2 id="add-domain-heading" className="text-base font-semibold text-zinc-900">
					Register a domain
				</h2>
				<form
					onSubmit={handleSubmit}
					className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:gap-4"
				>
					<div className="flex w-full flex-col gap-1 md:max-w-xs">
						<label htmlFor="affiliateId" className={LABEL}>
							Affiliate ID
						</label>
						<input
							id="affiliateId"
							name="affiliateId"
							type="text"
							className={INPUT}
							placeholder="aff_12345"
							value={draft.affiliateId}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, affiliateId: e.target.value }))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
						/>
						{draftErrors.affiliateId ? (
							<span className={ERROR_TEXT}>{draftErrors.affiliateId}</span>
						) : (
							<span className={HINT}>Network ID of the publisher.</span>
						)}
					</div>
					<div className="flex w-full flex-col gap-1">
						<label htmlFor="domain" className={LABEL}>
							Domain (FQDN)
						</label>
						<input
							id="domain"
							name="domain"
							type="text"
							className={INPUT}
							placeholder="clicks.publisher-brand.com"
							value={draft.domain}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, domain: e.target.value }))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
						/>
						{draftErrors.domain ? (
							<span className={ERROR_TEXT}>{draftErrors.domain}</span>
						) : (
							<span className={HINT}>
								Must be a fully-qualified hostname — no scheme, port, or path.
							</span>
						)}
					</div>
					<div className="flex items-end">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMutation.isPending}
						>
							{createMutation.isPending ? "Registering…" : "Register domain"}
						</button>
					</div>
				</form>

				{lastCreated ? (
					<div className="mt-6 rounded-[6px] border border-emerald-200 bg-emerald-50 p-4">
						<h3 className="text-sm font-semibold text-emerald-900">
							Add this TXT record to {lastCreated.domain}'s DNS zone
						</h3>
						<dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
							<dt className="text-emerald-800">Type</dt>
							<dd className="font-mono text-zinc-900">TXT</dd>
							<dt className="text-emerald-800">Name</dt>
							<dd className="break-all font-mono text-zinc-900">
								{lastCreated.txt_record_name}
							</dd>
							<dt className="text-emerald-800">Value</dt>
							<dd className="break-all font-mono text-zinc-900">
								{lastCreated.txt_record_value}
							</dd>
						</dl>
						<p className="mt-3 text-xs text-emerald-800">
							DNS can take a few minutes to propagate. Click <strong>Verify</strong>{" "}
							below once the record is live.
						</p>
					</div>
				) : null}
			</section>

			<section aria-labelledby="scope-heading" className={`${CARD} p-6`}>
				<h2 id="scope-heading" className="text-base font-semibold text-zinc-900">
					Registered domains
				</h2>
				<div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
					<div className="flex w-full flex-col gap-1 md:max-w-xs">
						<label htmlFor="scope" className={LABEL}>
							Affiliate ID to view
						</label>
						<input
							id="scope"
							type="text"
							className={INPUT}
							placeholder="aff_12345"
							value={scope}
							onChange={(e) => setScope(e.target.value)}
							autoComplete="off"
							spellCheck={false}
						/>
					</div>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => listQuery.refetch()}
						disabled={!canQuery || listQuery.isFetching}
					>
						{listQuery.isFetching ? "Refreshing…" : "Refresh"}
					</button>
				</div>

				{canQuery ? (
					<div className="mt-4 grid grid-cols-3 gap-3 md:max-w-md">
						<StatCell label="Pending" value={totals.pending} />
						<StatCell label="Verified" value={totals.verified} />
						<StatCell label="Failed" value={totals.failed} />
					</div>
				) : null}

				<div className="mt-6 overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
								<th className="px-4 py-2 font-medium">Domain</th>
								<th className="px-4 py-2 font-medium">Status</th>
								<th className="px-4 py-2 font-medium">TXT record</th>
								<th className="px-4 py-2 font-medium">Verified at</th>
								<th className="px-4 py-2 font-medium text-right">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{!canQuery ? (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										Enter an Affiliate ID to view their registered domains.
									</td>
								</tr>
							) : listQuery.isLoading ? (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										Loading domains…
									</td>
								</tr>
							) : listQuery.isError ? (
								<tr>
									<td colSpan={5} className="px-4 py-8 text-center text-sm text-red-600">
										{(listQuery.error as Error | undefined)?.message ??
											"Unable to load domains."}
									</td>
								</tr>
							) : sortedDomains.length === 0 ? (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										No domains registered for this affiliate yet.
									</td>
								</tr>
							) : (
								sortedDomains.map((d) => (
									<tr key={d.id}>
										<td className="break-all px-4 py-3 font-mono text-zinc-900">
											{d.domain}
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(d.status)}`}
											>
												{STATUS_LABELS[d.status]}
											</span>
										</td>
										<td className="break-all px-4 py-3 font-mono text-xs text-zinc-600">
											<div>{d.txt_record_name}</div>
											<div className="text-zinc-400">{d.txt_record_value}</div>
										</td>
										<td className="px-4 py-3 text-zinc-700">
											{formatVerifiedAt(d.verified_at)}
										</td>
										<td className="px-4 py-3 text-right">
											<div className="inline-flex gap-2">
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() => verifyMutation.mutate(d.id)}
													disabled={
														verifyMutation.isPending && verifyingId === d.id
													}
												>
													{verifyMutation.isPending && verifyingId === d.id
														? "Verifying…"
														: "Verify"}
												</button>
												<button
													type="button"
													className={BTN_DANGER}
													onClick={() => {
														if (
															typeof window !== "undefined" &&
															!window.confirm(
																`Remove ${d.domain}? Clicks routed through this hostname will stop resolving.`,
															)
														) {
															return;
														}
														deleteMutation.mutate(d.id);
													}}
													disabled={deleteMutation.isPending}
												>
													Remove
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				{lastOutcome && lastOutcome.kind === "mismatch" ? (
					<p className="mt-4 text-xs text-red-600">
						Last verification for <span className="font-mono">{lastOutcome.domain.domain}</span>{" "}
						found{" "}
						{lastOutcome.resolved.length > 0
							? `values: ${lastOutcome.resolved.join(", ")}`
							: "no TXT records"}
						. Expected <span className="font-mono">{lastOutcome.expected}</span>.
					</p>
				) : null}
			</section>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Local presentational widgets
// ---------------------------------------------------------------------------

function StatCell({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2">
			<div className="text-xs font-medium text-zinc-500">{label}</div>
			<div className="text-lg font-semibold text-zinc-900">{value}</div>
		</div>
	);
}
